/**
 * Proofing Flow Engine Types
 *
 * This module defines the type system for the proofing flow.
 * The flow is now DYNAMIC — driven by requirement templates from the database.
 * Product requirements are no longer hardcoded; they come from the backend.
 */

// Matches DB `proof_target` exactly - kept for store vs product distinction
export const ProofTarget = {
  STORE: "store",
  PRODUCT: "product",
} as const;
export type ProofTarget = (typeof ProofTarget)[keyof typeof ProofTarget];

/**
 * Product requirement from the database (mirrors product_requirements table)
 */
export interface ProductRequirement {
  id: number;
  code: string; // e.g., "angle_1", "angle_2"
  description: string; // Internal description
  labels: Record<string, string>; // Locale-aware labels: { en: "...", fr: "..." }
  order: number; // Sort order for sequential blocking
}

/**
 * Template information describing what proofs are required
 */
export interface TemplateInfo {
  requireStore: boolean; // Whether store proof is needed
  productRequirements: ProductRequirement[]; // Dynamic list of product requirements
}

/**
 * Runtime state of a single product requirement
 */
export interface ProductRequirementState {
  productRequirement: ProductRequirement; // Full requirement info
  isFulfilled: boolean; // Has pending or approved proof
  isBlocked: boolean; // Blocked by previous requirement (by order) or step
}

/**
 * Runtime state of the store step
 */
export interface StoreStepState {
  target: typeof ProofTarget.STORE;
  isRequired: boolean; // From template's requireStore flag
  isFulfilled: boolean; // Has pending or approved proof
  isBlocked: boolean; // Store step is never blocked (always first)
}

/**
 * Runtime state of a product step (one per store_activity)
 */
export interface ProductStepState {
  target: typeof ProofTarget.PRODUCT;
  storeActivityId: number;
  productName: string;
  isComplete: boolean; // All requirements fulfilled
  isBlocked: boolean; // Blocked by store step (if store is required and not fulfilled)
  requirements: ProductRequirementState[];
}

/**
 * Complete flow state for a single store
 */
export interface StoreFlowState {
  storeId: number;
  storeName: string;
  distance: number | null;
  totalPicturesNeeded: number; // Count of unfulfilled requirements
  isComplete: boolean; // All steps complete
  storeStep: StoreStepState;
  productSteps: ProductStepState[]; // One per store_activity
}
